/**
 * PropertyTypeFields — the visible "Property type" block on Property Details.
 *
 * The property type picks the listing's paperwork (offer form + disclosure
 * report), so it sits on Property Details rather than in the collapsed
 * Construction section (design §9.2). In a state with paperwork rules
 * (Wisconsin) it asks legal's follow-up questions, one at a time as each
 * answer requires, and shows what the answers mean:
 *   - ok: a read-only "Paperwork:" line (never a WB form number);
 *   - blocked: legal's block message with the attorney-referral line;
 *   - "I'm not sure": the can't-route-until-confirmed notice (a draft still
 *     saves, but the wizard and publishing stop);
 *   - unanswered: which question is left.
 *
 * Pure presentation: the parent owns the profile and the derivation result
 * (paperworkCategory.js), and applies answers with applyPaperworkAnswer so
 * questions that no longer apply are cleared.
 *
 * Props:
 *   - profile: the listing's mls_profile
 *   - result: deriveFromProfile(profile)
 *   - stateCode: the listing's state
 *   - required: show the required marker (true in a paperwork state)
 *   - onAnswer(key, value): a question or the type changed
 *   - notAvailable: the server says this paperwork can't be published yet
 */
import React, { useId, useState } from 'react';
import {
  STATUS_BLOCKED,
  STATUS_OK,
  normalizePropertyType,
  normalizeQualifiers,
  normalizeUnitCount,
} from './paperworkCategory';
import { copyText } from './paperworkCopy';
import { PROPERTY_TYPE_HINT_KEYS, PROPERTY_TYPE_OPTIONS, visiblePaperworkFields } from './mlsFieldSchema';
import {
  // Outside a paperwork state a hard block still can't be saved (L4 wording).
  GENERIC_BLOCK_MESSAGE,
  isPaperworkState,
  paperworkLine,
  paperworkReason,
  resultMessage,
} from './paperworkPresentation';
import styles from './PropertyTypeFields.module.css';

/** Shown when the server reports the listing's paperwork isn't live yet. */
const NOT_AVAILABLE_MESSAGE =
  "This paperwork isn't available on cutthe6.com yet. You can save your listing as a draft, but it can't be published until it is.";

function RadioQuestion({ field, ptype, value, onAnswer, idBase }) {
  const legendId = `${idBase}-${field.key}-label`;
  const helpId = `${idBase}-${field.key}-help`;
  return (
    <fieldset
      className={styles.question}
      aria-labelledby={legendId}
      aria-describedby={helpId}
      data-testid={`paperwork-q-${field.key}`}
    >
      <legend id={legendId} className={styles.questionLabel}>
        {copyText(field.labelKey(ptype))}
      </legend>
      <p id={helpId} className={styles.questionHelp}>
        {copyText(field.helpKey(ptype))}
      </p>
      <div className={styles.options}>
        {field.options(ptype).map((opt) => (
          <label key={opt.value} className={styles.option}>
            <input
              type="radio"
              name={`${idBase}-${field.key}`}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onAnswer(field.key, opt.value)}
            />
            <span>{copyText(opt.labelKey)}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function NumberQuestion({ field, value, onAnswer, idBase }) {
  const inputId = `${idBase}-${field.key}`;
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;
  const raw = value === undefined || value === null ? '' : String(value);
  const invalid = raw.trim() !== '' && normalizeUnitCount(raw) === null;
  return (
    <div className={styles.question} data-testid={`paperwork-q-${field.key}`}>
      <label htmlFor={inputId} className={styles.questionLabel}>
        {copyText(field.labelKey())}
      </label>
      <p id={helpId} className={styles.questionHelp}>
        {copyText(field.helpKey())}
      </p>
      <input
        id={inputId}
        className={`listing-form__input ${styles.numberInput}`}
        type="number"
        inputMode="numeric"
        min="1"
        step="1"
        value={raw}
        placeholder={copyText(field.placeholderKey)}
        onChange={(e) => onAnswer(field.key, e.target.value)}
        aria-describedby={invalid ? `${helpId} ${errorId}` : helpId}
        aria-invalid={invalid || undefined}
      />
      {invalid && (
        <p id={errorId} className={styles.fieldError} role="alert">
          {copyText(field.invalidKey)}
        </p>
      )}
    </div>
  );
}

export default function PropertyTypeFields({
  profile,
  result,
  stateCode,
  required = false,
  onAnswer,
  notAvailable = false,
}) {
  const idBase = `ptype${useId().replace(/:/g, '')}`;
  const [showFormsNote, setShowFormsNote] = useState(false);
  const selectedLabel = profile?.property_type || '';
  const ptype = normalizePropertyType(selectedLabel);
  const answers = normalizeQualifiers(profile);
  const paperworkState = isPaperworkState(stateCode);
  const questions = paperworkState ? visiblePaperworkFields(profile) : [];
  const hintKey = paperworkState ? PROPERTY_TYPE_HINT_KEYS[ptype] : null;

  // A legacy label not in today's list stays selectable, so an edit doesn't
  // silently change what was saved.
  const options =
    selectedLabel && !PROPERTY_TYPE_OPTIONS.includes(selectedLabel)
      ? [...PROPERTY_TYPE_OPTIONS, selectedLabel]
      : PROPERTY_TYPE_OPTIONS;

  const status = result?.status;
  const message = paperworkState ? resultMessage(stateCode, result) : null;
  const line = paperworkState ? paperworkLine(stateCode, result) : null;
  const reason = paperworkState ? paperworkReason(stateCode, result, profile) : null;
  const selectHelpId = `${idBase}-help`;
  const formsNoteId = `${idBase}-forms-note`;

  return (
    <section className={styles.block} aria-labelledby={`${idBase}-title`} data-testid="property-type-block">
      <h3 id={`${idBase}-title`} className="listing-form__section-title">
        {copyText('WI.paperwork.q.property_type.label')}
        {required && <span aria-hidden="true"> *</span>}
      </h3>
      {paperworkState && (
        <p id={selectHelpId} className="listing-form__hint">
          {copyText('WI.paperwork.q.property_type.help')}
        </p>
      )}

      <div className={styles.selectRow}>
        <select
          id={`${idBase}-select`}
          aria-labelledby={`${idBase}-title`}
          className="listing-form__input"
          value={selectedLabel}
          onChange={(e) => onAnswer('property_type', e.target.value)}
          aria-required={required || undefined}
          aria-describedby={paperworkState ? selectHelpId : undefined}
          data-testid="property-type-select"
        >
          {options.map((opt) => (
            <option key={opt || 'empty'} value={opt}>
              {opt || 'Select...'}
            </option>
          ))}
        </select>
        {hintKey && <p className={styles.typeHint}>{copyText(hintKey)}</p>}
      </div>

      {questions.map((field) =>
        field.type === 'number' ? (
          <NumberQuestion
            key={field.key}
            field={field}
            value={profile?.[field.key]}
            onAnswer={onAnswer}
            idBase={idBase}
          />
        ) : (
          <RadioQuestion
            key={field.key}
            field={field}
            ptype={ptype}
            value={answers[field.key] ?? ''}
            onAnswer={onAnswer}
            idBase={idBase}
          />
        ),
      )}

      {status === STATUS_BLOCKED && (
        <div className={`${styles.notice} ${styles.noticeBlocked}`} role="alert" data-testid="paperwork-blocked">
          <p className={styles.noticeText}>{message || GENERIC_BLOCK_MESSAGE}</p>
        </div>
      )}

      {paperworkState && status !== STATUS_OK && status !== STATUS_BLOCKED && ptype && message && (
        result.attorneyReferral ? (
          <div className={`${styles.notice} ${styles.noticeWarning}`} role="status" data-testid="paperwork-needs-info">
            <p className={styles.noticeText}>{message}</p>
          </div>
        ) : (
          <p className={styles.missing} data-testid="paperwork-missing">
            {message}
          </p>
        )
      )}

      {line && (
        <div className={styles.paperwork} data-testid="paperwork-line">
          <p className={styles.paperworkText}>
            {line}{' '}
            <button
              type="button"
              className={styles.infoButton}
              aria-expanded={showFormsNote}
              aria-controls={formsNoteId}
              aria-label="About cutthe6.com forms"
              title={copyText('WI.paperwork.forms_not_approved')}
              onClick={() => setShowFormsNote((v) => !v)}
            >
              i
            </button>
          </p>
          {reason && <p className={styles.paperworkReason}>{reason}</p>}
          {showFormsNote && (
            <p id={formsNoteId} className={styles.paperworkNote} data-testid="paperwork-forms-note">
              {copyText('WI.paperwork.forms_not_approved')}
            </p>
          )}
          {notAvailable && (
            <p className={styles.paperworkUnavailable} role="status" data-testid="paperwork-not-available">
              {NOT_AVAILABLE_MESSAGE}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
